/**
 * Pre-defined A2A Simulations
 * 
 * Demonstrates all 4 core A2A tasks:
 * 1. Capability Discovery
 * 2. Task Execution & State Management
 * 3. Secure Collaboration
 * 4. UX Negotiation
 */

export const simulations = {
  capability_discovery: {
    id: 'capability_discovery',
    name: 'Capability Discovery',
    description: 'Watch how agents discover each other\'s capabilities',
    difficulty: 'beginner',
    duration: '2 min',
    agents: [
      {
        id: 'client',
        name: 'Client Agent',
        type: 'client',
        capabilities: ['ui_interaction', 'data_visualization', 'user_input']
      },
      {
        id: 'remote',
        name: 'Remote Agent',
        type: 'remote',
        capabilities: ['generate_content', 'run_simulation', 'assess_understanding']
      }
    ],
    messages: [
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'capability-discovery',
        payload: {
          protocol: 'a2a/1.0',
          action: 'discover_capabilities'
        },
        explanation: 'Client Agent asks Remote Agent: "What can you do?" This is always the first step in A2A communication.',
        learningPoint: 'Capability Discovery must happen before any task execution',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'capabilities-list',
        payload: {
          capabilities: [
            {
              name: 'generate_learning_content',
              description: 'Generate educational content',
              parameters: ['topic', 'difficulty']
            },
            {
              name: 'simulate_a2a_interaction',
              description: 'Run A2A simulations',
              parameters: ['simulation_type', 'complexity']
            },
            {
              name: 'assess_understanding',
              description: 'Assess learner comprehension',
              parameters: ['topic', 'answers']
            }
          ]
        },
        explanation: 'Remote Agent responds with a complete list of its capabilities, including descriptions and required parameters.',
        learningPoint: 'Capabilities are described with name, description, and parameters',
        duration: 1500
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'capabilities-cached',
        payload: {
          status: 'cached',
          capability_count: 3
        },
        explanation: 'Client Agent stores these capabilities in memory for future use. No need to re-discover!',
        learningPoint: 'Clients cache capabilities to avoid redundant discovery requests',
        duration: 800
      }
    ],
    summary: [
      'Client requested Remote\'s capabilities',
      'Remote provided 3 capabilities with full details',
      'Client cached the capabilities for future use',
      'Total messages exchanged: 3'
    ]
  },

  task_execution: {
    id: 'task_execution',
    name: 'Task Execution with State Management',
    description: 'Execute a multi-step task while maintaining state',
    difficulty: 'intermediate',
    duration: '3 min',
    agents: [
      {
        id: 'client',
        name: 'Client Agent',
        type: 'client',
        capabilities: ['ui_interaction']
      },
      {
        id: 'remote',
        name: 'Remote Agent',
        type: 'remote',
        capabilities: ['generate_content', 'save_to_db']
      }
    ],
    messages: [
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'generate_learning_content',
        payload: {
          task: 'generate_learning_content',
          parameters: {
            topic: 'capability_discovery',
            difficulty: 'beginner',
            include_simulation: true
          },
          context: {
            learner_id: 'learner-123',
            session_id: 'session-456'
          }
        },
        explanation: 'Client requests content generation with specific parameters and context',
        learningPoint: 'Task execution includes parameters and context for personalization',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'task-accepted',
        payload: {
          status: 'accepted',
          task_id: 'task-789',
          estimated_duration: '15 seconds'
        },
        explanation: 'Remote Agent acknowledges the task and provides a task ID for tracking',
        learningPoint: 'Long-running tasks return a task ID immediately for async tracking',
        duration: 500
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'ai-processing',
        payload: {
          status: 'processing',
          step: 'generating_content',
          progress: 30
        },
        explanation: 'Remote Agent uses AI (DeepSeek) to generate educational content',
        learningPoint: 'Backend agents can send progress updates for long tasks',
        duration: 3000
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'database-save',
        payload: {
          status: 'processing',
          step: 'saving_to_database',
          progress: 80
        },
        explanation: 'Content is being saved to Supabase database',
        learningPoint: 'State is persisted in the database for reliability',
        duration: 1500
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'task-complete',
        payload: {
          status: 'completed',
          task_id: 'task-789',
          result: {
            module_id: 'mod-abc-123',
            title: 'Understanding Capability Discovery',
            sections: 5,
            quiz_questions: 4
          }
        },
        explanation: 'Task completed! Remote sends the generated content back to Client',
        learningPoint: 'Final response includes task_id for correlation with the original request',
        duration: 1000
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'ui-update',
        payload: {
          action: 'display_content',
          module_id: 'mod-abc-123'
        },
        explanation: 'Client Agent updates the UI to display the new learning content',
        learningPoint: 'Client agents are responsible for presenting results to users',
        duration: 800
      }
    ],
    summary: [
      'Client requested content generation',
      'Remote accepted and provided task ID',
      'AI processed the request (DeepSeek)',
      'Content saved to database',
      'Results returned to client',
      'UI updated with new content',
      'Total messages: 6, Duration: ~20 seconds'
    ]
  },

  secure_collaboration: {
    id: 'secure_collaboration',
    name: 'Secure Message Signing',
    description: 'See how agents secure their communication',
    difficulty: 'advanced',
    duration: '2 min',
    agents: [
      {
        id: 'client',
        name: 'Client Agent',
        type: 'client',
        capabilities: ['sign_messages', 'verify_signatures']
      },
      {
        id: 'remote',
        name: 'Remote Agent',
        type: 'remote',
        capabilities: ['verify_signatures', 'sign_responses']
      }
    ],
    messages: [
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'message-signing',
        payload: {
          action: 'signing_message',
          algorithm: 'HMAC-SHA256',
          secret_key: '***hidden***'
        },
        explanation: 'Client creates an HMAC-SHA256 signature of the message using a shared secret key',
        learningPoint: 'Messages are signed before transmission to prove authenticity',
        duration: 800
      },
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'execute-with-signature',
        payload: {
          task: 'assess_understanding',
          parameters: { topic: 'security', answers: [1, 3, 2] },
          signature: '8f4a9b2c1e6d...a3f7e9'
        },
        explanation: 'Signed request is sent with signature in the header',
        learningPoint: 'Signatures travel with messages, usually in HTTP headers',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'signature-verification',
        payload: {
          action: 'verifying_signature',
          received_signature: '8f4a9b2c1e6d...a3f7e9',
          computed_signature: '8f4a9b2c1e6d...a3f7e9',
          match: true
        },
        explanation: 'Remote Agent recomputes the signature and compares it to the received one',
        learningPoint: 'Both agents must have the same secret key for signature verification',
        duration: 1200
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'authentication-success',
        payload: {
          status: 'authenticated',
          agent_verified: true
        },
        explanation: 'Signatures match! The message is authentic and hasn\'t been tampered with',
        learningPoint: 'If signatures don\'t match, the message is rejected immediately',
        duration: 600
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'processing-task',
        payload: {
          status: 'processing',
          task: 'assess_understanding'
        },
        explanation: 'Only after authentication succeeds does the Remote Agent process the task',
        learningPoint: 'Security checks happen BEFORE task execution',
        duration: 2000
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'signing-response',
        payload: {
          action: 'signing_response',
          algorithm: 'HMAC-SHA256'
        },
        explanation: 'Remote signs its response before sending it back',
        learningPoint: 'Both directions of communication are signed',
        duration: 800
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'task-complete',
        payload: {
          result: { score: 100, feedback: 'Excellent!' },
          signature: 'b7c2e8f4a1d9...6e3f8a'
        },
        explanation: 'Signed response is sent back to Client',
        learningPoint: 'Client will verify this signature too',
        duration: 1000
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'verify-response',
        payload: {
          action: 'verifying_response_signature',
          match: true,
          status: 'trusted'
        },
        explanation: 'Client verifies Remote\'s signature - communication is secure end-to-end!',
        learningPoint: 'This prevents man-in-the-middle attacks',
        duration: 800
      }
    ],
    summary: [
      'Client signed request with HMAC-SHA256',
      'Remote verified signature successfully',
      'Task executed only after authentication',
      'Remote signed response',
      'Client verified response signature',
      'End-to-end secure communication achieved',
      'Total messages: 8'
    ]
  },

  ux_negotiation: {
    id: 'ux_negotiation',
    name: 'Dynamic UX Adaptation',
    description: 'Watch how agents negotiate the best user experience',
    difficulty: 'intermediate',
    duration: '2 min',
    agents: [
      {
        id: 'client',
        name: 'Client Agent',
        type: 'client',
        capabilities: ['detect_device', 'adapt_ui']
      },
      {
        id: 'remote',
        name: 'Remote Agent',
        type: 'remote',
        capabilities: ['recommend_ux', 'optimize_content']
      }
    ],
    messages: [
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'detect-context',
        payload: {
          device: 'mobile',
          screen_width: 375,
          screen_height: 667,
          bandwidth: '3g',
          user_level: 'beginner'
        },
        explanation: 'Client detects user\'s device and network context automatically',
        learningPoint: 'Context detection happens on the client side using browser APIs',
        duration: 500
      },
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'negotiate-ux',
        payload: {
          task: 'negotiate_ux',
          context: {
            device: 'mobile',
            screen_width: 375,
            bandwidth: '3g',
            user_level: 'beginner',
            preferred_language: 'en',
            theme: 'light'
          }
        },
        explanation: 'Client sends context to Remote and asks: "What\'s the best way to show content?"',
        learningPoint: 'UX negotiation helps optimize the experience for each user',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'ai-analysis',
        payload: {
          action: 'analyzing_context',
          using_ai: true,
          model: 'deepseek-chat'
        },
        explanation: 'Remote uses AI to analyze the context and determine optimal UI format',
        learningPoint: 'AI can make intelligent UX decisions based on context',
        duration: 1500
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'ux-recommendations',
        payload: {
          format: {
            layout: 'cards',
            data_granularity: 'summary',
            max_items_per_page: 5,
            include_help: true,
            enable_lazy_loading: true,
            font_size: 'large',
            show_visuals: true
          },
          reasoning: 'Mobile device with slow network and beginner user - prioritize simplicity and reduced data transfer'
        },
        explanation: 'Remote recommends card layout, large fonts, and help features for mobile beginner',
        learningPoint: 'Recommendations are context-specific and explain the reasoning',
        duration: 1000
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'apply-ux',
        payload: {
          action: 'applying_recommendations',
          changes: [
            'Switched to card layout',
            'Increased font size',
            'Enabled lazy loading',
            'Added help tooltips',
            'Limited items to 5 per page'
          ]
        },
        explanation: 'Client dynamically adapts its UI based on the recommendations',
        learningPoint: 'UI components adjust in real-time without page reload',
        duration: 1200
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'ux-optimized',
        payload: {
          status: 'optimized',
          user_experience: 'improved',
          load_time_saved: '45%'
        },
        explanation: 'UX optimization complete! The interface is now tailored for this specific user',
        learningPoint: 'Personalized UX can improve load times and user satisfaction by 40%+',
        duration: 800
      }
    ],
    summary: [
      'Client detected device and network context',
      'Sent context to Remote for UX recommendations',
      'AI analyzed context and suggested optimal format',
      'Client applied recommendations dynamically',
      'UI optimized for mobile beginner user',
      'Load time reduced by 45%',
      'Total messages: 6'
    ]
  },

  full_workflow: {
    id: 'full_workflow',
    name: 'Complete A2A Workflow',
    description: 'All 4 core tasks in one comprehensive simulation',
    difficulty: 'advanced',
    duration: '5 min',
    agents: [
      {
        id: 'client',
        name: 'Client Agent',
        type: 'client',
        capabilities: ['ui_interaction', 'detect_device', 'sign_messages']
      },
      {
        id: 'remote',
        name: 'Remote Agent',
        type: 'remote',
        capabilities: ['generate_content', 'verify_signatures', 'recommend_ux']
      }
    ],
    messages: [
      // Task 1: Capability Discovery
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'discover-capabilities',
        payload: { action: 'discover_capabilities' },
        explanation: '🔍 TASK 1: Capability Discovery - Client asks what Remote can do',
        learningPoint: 'Always start with capability discovery',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'capabilities-list',
        payload: { capabilities: ['generate_content', 'assess', 'recommend_ux'] },
        explanation: '✅ Remote provides its 3 capabilities',
        learningPoint: 'Capabilities are cached for future use',
        duration: 1000
      },

      // Task 4: UX Negotiation
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'negotiate-ux',
        payload: {
          context: { device: 'desktop', user_level: 'intermediate' }
        },
        explanation: '🎨 TASK 4: UX Negotiation - Client asks for optimal UI format',
        learningPoint: 'UX negotiation can happen before or after task execution',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'ux-format',
        payload: { format: { layout: 'table', items_per_page: 10 } },
        explanation: '✅ Remote recommends table layout for desktop',
        learningPoint: 'Desktop users get more data-dense layouts',
        duration: 1000
      },

      // Task 3: Secure Collaboration
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'signing-request',
        payload: { algorithm: 'HMAC-SHA256' },
        explanation: '🔒 TASK 3: Secure Collaboration - Client signs the request',
        learningPoint: 'All production messages should be signed',
        duration: 800
      },

      // Task 2: Task Execution
      {
        sender: 'client',
        receiver: 'remote',
        messageType: 'request',
        task: 'generate-content',
        payload: {
          task: 'generate_learning_content',
          parameters: { topic: 'a2a_basics' },
          signature: '...'
        },
        explanation: '⚡ TASK 2: Task Execution - Client requests content generation',
        learningPoint: 'Task execution is the main purpose of A2A systems',
        duration: 1000
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'verify-signature',
        payload: { verified: true },
        explanation: '🔒 Remote verifies signature before processing',
        learningPoint: 'Security verification happens before every task',
        duration: 800
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'task-accepted',
        payload: { task_id: 'task-001', status: 'accepted' },
        explanation: '✅ Task accepted, Remote provides task ID',
        learningPoint: 'Task IDs allow async tracking of long-running tasks',
        duration: 800
      },
      {
        sender: 'remote',
        receiver: 'remote',
        messageType: 'notification',
        task: 'ai-processing',
        payload: { progress: 50 },
        explanation: '🤖 AI is generating content...',
        learningPoint: 'Progress updates keep users informed',
        duration: 3000
      },
      {
        sender: 'remote',
        receiver: 'client',
        messageType: 'response',
        task: 'task-complete',
        payload: { result: { module_id: 'mod-001' }, signature: '...' },
        explanation: '✅ Task complete! Signed response sent back',
        learningPoint: 'Responses are also signed for two-way security',
        duration: 1000
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'verify-response',
        payload: { verified: true },
        explanation: '🔒 Client verifies response signature',
        learningPoint: 'End-to-end security verification',
        duration: 800
      },
      {
        sender: 'client',
        receiver: 'client',
        messageType: 'notification',
        task: 'ui-update',
        payload: { action: 'display_content' },
        explanation: '🎨 UI updated with new content in recommended format',
        learningPoint: 'Complete A2A workflow executed successfully!',
        duration: 1000
      }
    ],
    summary: [
      '✅ Task 1: Capability Discovery completed',
      '✅ Task 4: UX Negotiation completed',
      '✅ Task 3: Secure Collaboration applied',
      '✅ Task 2: Task Execution completed',
      'All 4 core A2A tasks demonstrated',
      'Total messages: 12',
      'Total duration: ~30 seconds',
      'This is how production A2A systems work!'
    ]
  }
};

export default simulations;

